import { useEffect, useState } from "react";
import Navigation from "../components/Navigation";
import ProgressRing from "../components/ProgressRing";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Sparkles, MapPin, Wallet, Calendar, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function PanelistDashboard() {
  const { user, profile, refresh } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [{ data: d }, { data: q }] = await Promise.all([
        api.get("/panelist/dashboard"),
        api.get("/panelist/lifestyle-questions"),
      ]);
      setDashboard(d);
      setQuestions(q.questions);
    } catch {
      // Dashboard load failure — leave existing state untouched; user can refresh.
    }
  };

  useEffect(() => { load(); }, []);

  const answerQuestion = async (qid, ans) => {
    setSaving(true);
    try {
      await api.post("/panelist/lifestyle-answer", { question_id: qid, answer: ans });
      await Promise.all([load(), refresh()]);
    } finally {
      setSaving(false);
    }
  };

  const p = dashboard?.profile ?? profile;
  const completes = p?.profile_completeness ?? 40;
  const hasCampaigns = (dashboard?.available_campaigns?.length || 0) > 0;

  return (
    <div className="min-h-screen bg-[var(--rl-bg-subtle)]" data-testid="panelist-dashboard">
      <Navigation />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-[1fr_320px] gap-8 items-start">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-brand)]">Panelist Hub</div>
            <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tight mt-2">Hi, {p?.full_name?.split(" ")[0] || "Panelist"}.</h1>
            <p className="text-[var(--rl-text-secondary)] mt-3 leading-relaxed">{hasCampaigns ? "You have studies waiting. Each earns $2.00 CAD and takes ~5 minutes." : "We'll route studies your way the moment they match. Meanwhile, beef up your profile to unlock more."}</p>

            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              <Stat icon={<MapPin className="w-4 h-4" />} label="Region" value={p?.geographic_region || "—"} testId="stat-region" />
              <Stat icon={<Wallet className="w-4 h-4" />} label="Lifetime earnings" value={`$${(p?.lifetime_earnings ?? 0).toFixed(2)} CAD`} testId="stat-earnings" />
              <Stat icon={<Calendar className="w-4 h-4" />} label="Studies this month" value={`${p?.surveys_this_month ?? 0} / 4`} testId="stat-monthly" />
            </div>

            {/* Available campaigns */}
            {hasCampaigns ? (
              <div className="mt-10">
                <h2 className="font-display font-bold text-2xl tracking-tight">Available studies</h2>
                <div className="mt-4 space-y-3" data-testid="available-campaigns-list">
                  {dashboard.available_campaigns.map((c) => (
                    <Link
                      key={c.id}
                      to={`/micro-research/run?campaignId=${c.id}&source=internal&pid=${user?.id}`}
                      className="flex items-center justify-between bg-white border border-[var(--rl-border)] hover:border-[var(--rl-brand)] p-5 transition-colors"
                      data-testid={`campaign-row-${c.id}`}
                    >
                      <div>
                        <div className="font-semibold">{c.title}</div>
                        <div className="text-xs text-[var(--rl-text-secondary)] mt-1">~{c.estimated_minutes} min • Earn {c.reward}</div>
                      </div>
                      <ArrowRight className="w-5 h-5 text-[var(--rl-brand)]" />
                    </Link>
                  ))}
                </div>
              </div>
            ) : questions.length === 0 ? (
              // All lifestyle questions answered — render a clean "all-done" card
              // instead of the misleading "Unlock more studies" lead-in followed
              // by an empty list. (Panelist was scrolling for questions that
              // weren't there.)
              <div className="mt-10 bg-white border border-[var(--rl-border)] p-8" data-testid="empty-state-lifestyle-done">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5 text-[var(--rl-success)]" />
                  <h2 className="font-display font-bold text-2xl tracking-tight">You're all set</h2>
                </div>
                <p className="text-sm text-[var(--rl-text-secondary)] mt-3 max-w-xl leading-relaxed" data-testid="lifestyle-done-copy">
                  You've answered every lifestyle question we have right now. Your profile is fully tagged — we'll match new studies the moment they go live. Watch your email and SMS for invitations.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button
                    onClick={load}
                    disabled={saving}
                    className="inline-flex items-center gap-2 bg-[var(--rl-text)] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[var(--rl-brand)] transition-colors disabled:opacity-50"
                    data-testid="lifestyle-refresh-btn"
                  >
                    <ArrowRight className="w-4 h-4" /> Check for new studies
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-10 bg-white border border-[var(--rl-border)] p-8" data-testid="empty-state-profile">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5 text-[var(--rl-accent)]" />
                  <h2 className="font-display font-bold text-2xl tracking-tight">Unlock more studies</h2>
                </div>
                <p className="text-sm text-[var(--rl-text-secondary)] mt-3 max-w-xl leading-relaxed">Each quick answer below adds a lifestyle tag to your profile — increasing how many studies match you. No reward, but compounding upside.</p>

                <div className="mt-8 space-y-6">
                  {questions.slice(0, 4).map((q) => (
                    <div key={q.id} data-testid={`lifestyle-q-${q.id}`}>
                      <div className="text-sm font-semibold mb-3">{q.text}</div>
                      <div className="flex flex-wrap gap-2">
                        {q.options.map((opt) => (
                          <button
                            key={opt}
                            disabled={saving}
                            onClick={() => answerQuestion(q.id, opt)}
                            className="px-4 py-2 text-sm border border-[var(--rl-border)] bg-white hover:bg-[var(--rl-text)] hover:text-white hover:border-[var(--rl-text)] transition-colors disabled:opacity-50"
                            data-testid={`lifestyle-option-${q.id}-${opt}`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right rail */}
          <aside className="bg-white border border-[var(--rl-border)] p-8 lg:sticky lg:top-24" data-testid="profile-completion-card">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">Profile completeness</div>
            <div className="mt-6 flex justify-center">
              <ProgressRing value={completes} size={150} stroke={10} sublabel="complete" />
            </div>
            <div className="mt-6 text-xs text-[var(--rl-text-secondary)] leading-relaxed">
              Higher completeness = more matching studies. Answer the lifestyle prompts on the left to climb.
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, testId }) {
  return (
    <div className="bg-white border border-[var(--rl-border)] p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[var(--rl-text-secondary)]">{icon}{label}</div>
      <div className="font-display font-bold text-xl mt-2">{value}</div>
    </div>
  );
}
